/**
 * Filters section for managing linkedTable filters
 */

import { useMemo } from 'react';
import { CALCULATED_TABLE_ALIAS, type TableFieldApiResponse } from '@loader/shared-types';
import { Plus } from 'lucide-react';
import { Button } from '@loader/ui';

import { FilterEditor } from '@/features/linkedTables/editLinkedTable/components/FilterEditor';
import type { LinkedTableCalculatedColumn, LinkedTableFilter, LinkedTableVariable } from '@/lib/types';

/** Order index for calculated columns to place them at the end of the list */
const CALCULATED_COLUMN_ORDER_INDEX = 999;

interface FiltersSectionProps {
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
  filters: LinkedTableFilter[];
  availableFields: TableFieldApiResponse[];
  /** Calculated columns available for filtering */
  calculated_columns: LinkedTableCalculatedColumn[];
  linkedTableId?: string; // LinkedTable ID to fetch field values from (unfiltered)
  /** Unified version selections keyed by table alias */
  versionSelections?: Record<string, string>;
  incompleteFilterIds?: string[]; // IDs of incomplete filters to highlight
  /** Variables available for filter binding */
  variables?: LinkedTableVariable[];
  onAddFilter?: () => void;
  onUpdateFilter?: (filter: LinkedTableFilter) => void;
  onRemoveFilter?: (filter: LinkedTableFilter) => void;
}

/**
 * Section for managing linkedTable filters
 */
export function FiltersSection({
  canEdit = true,
  filters,
  availableFields,
  calculated_columns,
  linkedTableId,
  versionSelections,
  incompleteFilterIds = [],
  variables = [],
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
}: FiltersSectionProps) {
  // Merge calculated columns into available fields for the filter dropdown
  // Calculated columns use CALCULATED_TABLE_ALIAS as their table_alias
  const allFilterableFields = useMemo(() => {
    const calcFields: TableFieldApiResponse[] = calculated_columns
      .filter((col) => col.id && col.name) // Only include complete calculated columns
      .map((col) => ({
        id: col.id,
        name: col.name,
        data_type: col.data_type || 'text',
        table_id: CALCULATED_TABLE_ALIAS,
        table_alias: CALCULATED_TABLE_ALIAS,
        is_enabled: true,
        is_required: false,
        is_unique: false,
        default_value: null,
        validation_rules: null,
        description: col.description || `Calculated: ${col.formula_value || col.formula}`,
        order_index: CALCULATED_COLUMN_ORDER_INDEX,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as TableFieldApiResponse));

    return [...availableFields, ...calcFields];
  }, [availableFields, calculated_columns]);

  return (
    <div className="space-y-3">
      {/* Render existing filters */}
      {filters.length === 0 && (
        <div className="text-muted-foreground py-4 text-center text-sm">
          No filters defined
        </div>
      )}
      {filters.map((filter) => (
        <FilterEditor
          key={filter.id}
          canEdit={canEdit}
          filter={filter}
          availableFields={allFilterableFields}
          linkedTableId={linkedTableId}
          versionSelections={versionSelections}
          isIncomplete={incompleteFilterIds.includes(filter.id)}
          variables={variables}
          onUpdate={onUpdateFilter ?? (() => {})}
          onRemove={onRemoveFilter ?? (() => {})}
        />
      ))}

      {/* Add Filter Button - Hidden for viewers */}
      {canEdit && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onAddFilter}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Filter
        </Button>
      )}
    </div>
  );
}

